import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LuxuryMarquee } from "@/components/LuxuryMarquee";
import { ScentFinderBar } from "@/components/ScentFinderBar";
import { OlfactoryPyramid } from "@/components/OlfactoryPyramid";
import { usePrefersReducedMotion, useIsTouch } from "@/hooks/usePrefersReducedMotion";
import { ArrowRight, Sparkles } from "lucide-react";
import { fetchProducts } from "@/lib/api";
import { useStore } from "@/lib/store";
import { useSiteContent } from "@/lib/siteContent";
import { ProductCard } from "@/components/ProductCard";

export default function HomePage() {
  const prefersReducedMotion = usePrefersReducedMotion();
  const isTouch = useIsTouch();
  const { country } = useStore();
  const content = useSiteContent();
  const { hero, site_settings: site, sections, testimonials } = content;
  const heroRef = useRef(null);
  const glowRef = useRef(null);
  const [heroPointerActive, setHeroPointerActive] = useState(false);
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    fetchProducts({ country }).then((data) => setFeatured((data || []).slice(0, 8))).catch(() => {});
  }, [country]);

  useEffect(() => {
    if (prefersReducedMotion || isTouch) return;
    const el = heroRef.current;
    const glow = glowRef.current;
    if (!el || !glow) return;
    const onMove = (e) => {
      const rect = el.getBoundingClientRect();
      glow.style.left = `${e.clientX - rect.left}px`;
      glow.style.top = `${e.clientY - rect.top}px`;
      glow.style.opacity = "1";
    };
    const onLeave = () => { glow.style.opacity = "0"; };
    el.addEventListener("mousemove", onMove);
    el.addEventListener("mouseleave", onLeave);
    return () => { el.removeEventListener("mousemove", onMove); el.removeEventListener("mouseleave", onLeave); };
  }, [prefersReducedMotion, isTouch]);

  return (
    <main data-testid="home-page">
      {/* ---- HERO (DARK CINEMATIC) ---- */}
      <section ref={heroRef} className="relative overflow-hidden sd-dark sd-dark-noise">
        <div ref={glowRef} className="sd-cursor-glow" style={{ opacity: 0 }} />
        {/* Corner amber glows for depth */}
        <div className="absolute -top-32 -left-32 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none" />
        <div className="absolute -bottom-24 -right-24 w-[420px] h-[420px] sd-amber-glow-dark rounded-full pointer-events-none opacity-70" />

        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 pt-4 sm:pt-8 pb-10 sm:pb-14 lg:pb-24 grid lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-4 items-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-7 relative z-10 order-2 lg:order-1"
          >
            <div className="inline-flex items-center gap-2 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#D4B278] mb-4 sm:mb-6">
              <Sparkles className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
              <span>{hero.eyebrow}</span>
            </div>
            <h1 className="font-serif text-[40px] sm:text-[56px] lg:text-[84px] leading-[1.02] tracking-[-0.01em] text-[#FAF8F5]">
              {hero.headline_line1}<br />{hero.headline_line2}<span className="italic text-[#D4B278] font-normal"> {hero.headline_accent}</span>
            </h1>
            <p className="mt-5 sm:mt-6 max-w-md text-[#FAF8F5]/75 text-[15px] sm:text-base leading-relaxed">
              {hero.body}
            </p>
            <div className="mt-7 sm:mt-9 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Link
                to={hero.cta_primary_link || "/shop"}
                data-testid="hero-explore-collection-button"
                className="inline-flex items-center justify-center gap-2 bg-[#C5A059] text-[#0B0A08] px-6 py-4 sm:py-3.5 rounded-xl text-sm uppercase tracking-[0.18em] hover:bg-[#D4B278] transition-all shadow-[0_10px_36px_rgba(197,160,89,0.35)] hover:-translate-y-[1px]"
              >
                {hero.cta_primary_label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to={hero.cta_secondary_link || "/quiz"}
                data-testid="hero-take-quiz-button"
                className="inline-flex items-center justify-center gap-2 border border-[#D4B278]/60 text-[#FAF8F5] px-6 py-4 sm:py-3.5 rounded-xl text-sm uppercase tracking-[0.18em] hover:bg-[#D4B278]/12 hover:border-[#D4B278] transition-colors"
              >
                {hero.cta_secondary_label}
              </Link>
            </div>

            <div className="mt-8 sm:mt-10 flex items-center gap-4 sm:gap-6 text-[10px] sm:text-xs text-[#FAF8F5]/55 uppercase tracking-[0.14em] sm:tracking-[0.16em]">
              {(hero.trust_bar || []).map((item, i) => (
                <React.Fragment key={item}>
                  {i > 0 && <div className="h-3 w-px bg-white/15" />}
                  <div>{item}</div>
                </React.Fragment>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative order-1 lg:order-2"
          >
            <div className="relative h-[380px] sm:h-[560px] lg:h-[680px] w-full flex items-center justify-center">
              {/* Ambient amber glow behind the bottle */}
              <div
                className={`absolute inset-8 sd-amber-glow-dark rounded-full pointer-events-none ${prefersReducedMotion ? "" : "sd-smoke-drift"}`}
                style={{ opacity: 0.6 }}
              />

              {/* Large maison mark — floating at top-left of the hero image area */}
              <motion.img
                src={site.logo_image_url || "/scentodore-logo.webp"}
                alt="Scentodore maison mark"
                draggable={false}
                initial={{ opacity: 0, y: -14, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.1, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
                data-testid="hero-logo-mark"
                className={`absolute top-2 left-2 sm:top-4 sm:left-4 lg:-top-2 lg:-left-6 xl:-left-10 z-30 h-24 sm:h-32 lg:h-40 xl:h-48 w-auto select-none pointer-events-none ${prefersReducedMotion ? "" : "sd-logo-float"}`}
                style={{
                  filter:
                    "drop-shadow(0 12px 24px rgba(0,0,0,0.55)) drop-shadow(0 0 32px rgba(197,160,89,0.35))",
                }}
              />

              {/* Vertical typographic accent — left */}
              <div className="hidden lg:flex absolute left-0 top-1/2 -translate-y-1/2 flex-col items-center gap-3 select-none z-20">
                <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.42em] text-[#D4B278]/80">
                  {hero.vertical_left_top}
                </span>
                <span className="h-16 w-px bg-gradient-to-b from-[#D4B278]/60 to-transparent" />
                <span className="[writing-mode:vertical-rl] rotate-180 text-[10px] uppercase tracking-[0.42em] text-[#FAF8F5]/45">
                  {hero.vertical_left_bottom}
                </span>
              </div>

              {/* Vertical typographic accent — right */}
              <div className="hidden lg:flex absolute right-0 top-1/2 -translate-y-1/2 flex-col items-center gap-3 select-none z-20">
                <span className="[writing-mode:vertical-rl] text-[10px] uppercase tracking-[0.42em] text-[#FAF8F5]/45">
                  {hero.vertical_right_top}
                </span>
                <span className="h-16 w-px bg-gradient-to-b from-[#D4B278]/60 to-transparent" />
                <span className="[writing-mode:vertical-rl] text-[10px] uppercase tracking-[0.42em] text-[#D4B278]/80">
                  {hero.vertical_right_bottom}
                </span>
              </div>

              {/* Dark hero shot — feathered edges blend into surrounding dark hero */}
              <img
                src={site.hero_bottle_image_url || "/scentodore-hero-bottle.webp"}
                srcSet={`${site.hero_bottle_image_url || "/scentodore-hero-bottle.webp"} 1x, ${site.hero_bottle_image_url || "/scentodore-hero-bottle-2x.webp"} 2x`}
                alt="Scentodore signature bottle in cinematic light"
                loading="eager"
                fetchpriority="high"
                data-testid="hero-bottle-image"
                className={`relative z-10 h-full w-auto max-w-[92%] object-contain sd-hero-blend ${prefersReducedMotion ? "" : "sd-hero-breathe"}`}
                draggable={false}
              />
            </div>
          </motion.div>
        </div>

        <div className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 pb-12 sm:pb-16 lg:pb-24 relative z-10 sd-dark-scent-finder">
          <ScentFinderBar variant="dark" />
        </div>
      </section>

      {/* ---- LUXURY MARQUEE (Ingredients + Offers) ---- */}
      <LuxuryMarquee />

      {/* ---- FEATURED FRAGRANCES ---- */}
      <section className="max-w-[1280px] mx-auto px-5 sm:px-6 lg:px-10 py-12 sm:py-14 lg:py-20">
        <div className="flex items-end justify-between mb-6 sm:mb-10">
          <div>
            <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-1.5 sm:mb-2">{sections.featured_eyebrow}</div>
            <h2 className="font-serif text-2xl sm:text-3xl md:text-5xl text-[#1A1918]">{sections.featured_title}</h2>
          </div>
          <Link to="/shop" data-testid="home-view-all-link" className="text-xs sm:text-sm text-[#1A1918] hover:text-[#C5A059] transition-colors underline underline-offset-4 decoration-[#C5A059]/60">{sections.featured_view_all_label}</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 sm:gap-5">
          {featured.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>
      </section>

      {/* ---- OLFACTORY PYRAMID ---- */}
      <section className="bg-[#F1ECE4]/50 sd-noise">
        <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
          <OlfactoryPyramid />
        </div>
      </section>

      {/* ---- SOCIAL PROOF ---- */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid lg:grid-cols-3 gap-8 items-center">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C5A059] mb-3">{sections.social_proof_eyebrow}</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1918] leading-[1.1]">{sections.social_proof_title}</h2>
            <p className="mt-5 text-[#1A1918]/70 leading-relaxed max-w-md">{sections.social_proof_body}</p>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-3 gap-4">
            {(testimonials || []).map((t, idx) => {
              const author = t.author || t.who || `Guest ${idx + 1}`;
              const badgeText = t.badge || "Verified";
              const quote = t.quote || t.q || "";
              const testId = author.split(" ")[0].toLowerCase();
              return (
                <div key={`${author}-${idx}`} data-testid={`testimonial-${testId}`} className="p-6 rounded-2xl border border-black/10 bg-[#FAF8F5]">
                  <div className="text-[15px] font-serif leading-snug">“{quote}”</div>
                  <div className="mt-4 flex items-center justify-between text-xs">
                    <div className="text-[#1A1918]/70">{author}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] bg-[#C5A059]/15 text-[#C5A059] rounded-full px-2 py-0.5">{badgeText}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---- CTA STRIP ---- */}
      <section className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-10 pb-16 lg:pb-24">
        <div className="rounded-3xl p-10 lg:p-16 relative overflow-hidden border border-black/10" style={{ background: "linear-gradient(135deg, #F1ECE4 0%, #FAF8F5 100%)" }}>
          <div className="absolute -top-20 -right-20 w-[300px] h-[300px] sd-amber-glow rounded-full" />
          <div className="relative z-10 max-w-2xl">
            <h2 className="font-serif text-3xl sm:text-5xl text-[#1A1918] leading-[1.05]">{sections.cta_strip_title}</h2>
            <p className="mt-4 text-[#1A1918]/70 max-w-md">{sections.cta_strip_body}</p>
            <Link
              to="/quiz"
              data-testid="cta-take-quiz"
              className="mt-8 inline-flex items-center gap-2 bg-[#1A1918] text-[#FAF8F5] px-6 py-3.5 rounded-xl text-sm uppercase tracking-[0.18em] hover:bg-black transition-colors"
            >
              {sections.cta_strip_button} <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
